import { useState, type FormEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AlertBanner,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Heading,
  Input,
  SelectField,
  Text,
  tabularNumsClass,
} from "@lendcord/concord";
import { ProfileField } from "@/features/profile/components/ProfileField";
import { EmploymentSectionSkeleton } from "./EmploymentSectionSkeleton";
import { isQueryApiError } from "@/app/store/baseApi";
import {
  useCreateEmploymentDetailMutation,
  useDeleteEmploymentDetailMutation,
  useGetProfileQuery,
  useUpdateEmploymentDetailMutation,
} from "@/app/profile/api/profile.api";
import type { EmploymentType, Profile } from "@/app/profile/profile.types";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const EMPLOYMENT_TYPE_LABEL_IDS: Record<EmploymentType, string> = {
  SALARIED: "lender.profile.employmentTypeSalaried",
  SELF_EMPLOYED: "lender.profile.employmentTypeSelfEmployed",
  BUSINESS: "lender.profile.employmentTypeBusiness",
  UNEMPLOYED: "lender.profile.employmentTypeUnemployed",
  RETIRED: "lender.profile.employmentTypeRetired",
  STUDENT: "lender.profile.employmentTypeStudent",
  OTHER: "lender.profile.employmentTypeOther",
};

const EMPLOYMENT_TYPE_OPTIONS: EmploymentType[] = [
  "SALARIED",
  "SELF_EMPLOYED",
  "BUSINESS",
  "UNEMPLOYED",
  "RETIRED",
  "STUDENT",
  "OTHER",
];

interface EmploymentSectionProps {
  profile: Profile | null;
}

export function EmploymentSection({ profile }: EmploymentSectionProps) {
  const intl = useIntl();
  const { refetch } = useGetProfileQuery();
  const [createEmploymentDetail] = useCreateEmploymentDetailMutation();
  const [updateEmploymentDetail] = useUpdateEmploymentDetailMutation();
  const [deleteEmploymentDetail, { isLoading: isDeletingEmployment }] = useDeleteEmploymentDetailMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [employmentType, setEmploymentType] = useState<EmploymentType | "">("");
  const [employerName, setEmployerName] = useState("");
  const [designation, setDesignation] = useState("");
  const [highestQualification, setHighestQualification] = useState("");
  const [monthlyIncome, setMonthlyIncome] = useState("");
  const [errors, setErrors] = useState<{ employmentType?: string; monthlyIncome?: string }>({});
  const [formError, setFormError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);
  const [removeError, setRemoveError] = useState<string | undefined>();

  if (!profile) {
    return <EmploymentSectionSkeleton />;
  }

  const employment = profile.employmentDetail;

  function handleStartEdit() {
    setEmploymentType(employment?.employmentType ?? "");
    setEmployerName(employment?.employerName ?? "");
    setDesignation(employment?.designation ?? "");
    setHighestQualification(employment?.highestQualification ?? "");
    setMonthlyIncome(employment?.monthlyIncome != null ? String(employment.monthlyIncome) : "");
    setErrors({});
    setFormError(undefined);
    setIsEditing(true);
  }

  function handleCancel() {
    setIsEditing(false);
  }

  async function handleRemove() {
    setRemoveError(undefined);
    try {
      await deleteEmploymentDetail().unwrap();
      await refetch();
      setIsRemoveConfirmOpen(false);
    } catch (error) {
      setRemoveError(
        isQueryApiError(error) ? error.message : intl.formatMessage({ id: "lender.profile.removeEmploymentError" }),
      );
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(undefined);

    const nextErrors: typeof errors = {};
    if (!employmentType) {
      nextErrors.employmentType = intl.formatMessage({ id: "lender.profile.employmentTypeRequiredError" });
    }
    if (monthlyIncome && (Number.isNaN(Number(monthlyIncome)) || Number(monthlyIncome) < 0)) {
      nextErrors.monthlyIncome = intl.formatMessage({ id: "lender.profile.monthlyIncomeFormatError" });
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const payload = {
      employmentType: employmentType as EmploymentType,
      employerName: employerName.trim() || undefined,
      designation: designation.trim() || undefined,
      highestQualification: highestQualification.trim() || undefined,
      monthlyIncome: monthlyIncome ? Number(monthlyIncome) : undefined,
    };

    setIsSubmitting(true);
    try {
      if (employment) {
        await updateEmploymentDetail(payload).unwrap();
      } else {
        await createEmploymentDetail(payload).unwrap();
      }
      await refetch();
      setIsEditing(false);
    } catch {
      setFormError(intl.formatMessage({ id: "lender.profile.employmentUpdateError" }));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <Heading level={3}>
          <FormattedMessage id="lender.profile.employmentDetailsHeading" />
        </Heading>
        {!isEditing && (
          <div className="flex gap-2">
            {employment && (
              <Button
                type="button"
                variant="ghost"
                density="compact"
                className="text-error-fg hover:bg-error-bg"
                onClick={() => setIsRemoveConfirmOpen(true)}
              >
                <FormattedMessage id="lender.profile.removeEmploymentButton" />
              </Button>
            )}
            <Button type="button" variant="ghost" density="compact" onClick={handleStartEdit}>
              <FormattedMessage id={employment ? "lender.profile.editButton" : "lender.profile.addButton"} />
            </Button>
          </div>
        )}
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
          {formError && <AlertBanner status="error">{formError}</AlertBanner>}

          <SelectField
            label={intl.formatMessage({ id: "lender.profile.employmentTypeLabel" })}
            placeholder={intl.formatMessage({ id: "lender.profile.employmentTypePlaceholder" })}
            value={employmentType}
            onValueChange={(value) => setEmploymentType(value as EmploymentType)}
            error={errors.employmentType}
            required
            options={EMPLOYMENT_TYPE_OPTIONS.map((type) => ({
              value: type,
              label: intl.formatMessage({ id: EMPLOYMENT_TYPE_LABEL_IDS[type] }),
            }))}
          />

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <FormField label={intl.formatMessage({ id: "lender.profile.employerNameLabel" })}>
              <Input value={employerName} onChange={(event) => setEmployerName(event.target.value)} />
            </FormField>
            <FormField label={intl.formatMessage({ id: "lender.profile.designationLabel" })}>
              <Input value={designation} onChange={(event) => setDesignation(event.target.value)} />
            </FormField>
          </div>

          <FormField label={intl.formatMessage({ id: "lender.profile.highestQualificationLabel" })}>
            <Input
              value={highestQualification}
              onChange={(event) => setHighestQualification(event.target.value)}
            />
          </FormField>

          <FormField
            label={intl.formatMessage({ id: "lender.profile.monthlyIncomeLabel" })}
            error={errors.monthlyIncome}
          >
            <Input
              value={monthlyIncome}
              onChange={(event) => setMonthlyIncome(event.target.value.replace(/[^\d]/g, ""))}
              inputMode="numeric"
            />
          </FormField>

          <div className="flex gap-3">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <FormattedMessage id="lender.profile.savingButton" />
              ) : (
                <FormattedMessage id="lender.profile.saveButton" />
              )}
            </Button>
            <Button type="button" variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
              <FormattedMessage id="lender.profile.cancelButton" />
            </Button>
          </div>
        </form>
      ) : employment ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <ProfileField
            labelId="lender.profile.employmentTypeLabel"
            value={intl.formatMessage({ id: EMPLOYMENT_TYPE_LABEL_IDS[employment.employmentType] })}
          />
          <ProfileField
            labelId="lender.profile.monthlyIncomeLabel"
            value={employment.monthlyIncome != null ? currencyFormatter.format(employment.monthlyIncome) : "–"}
            valueClassName={tabularNumsClass}
          />
          <ProfileField labelId="lender.profile.employerNameLabel" value={employment.employerName ?? "–"} />
          <ProfileField labelId="lender.profile.designationLabel" value={employment.designation ?? "–"} />
          <ProfileField
            labelId="lender.profile.highestQualificationLabel"
            value={employment.highestQualification ?? "–"}
          />
        </div>
      ) : (
        <Text size="sm" variant="muted">
          <FormattedMessage id="lender.profile.noEmploymentMessage" />
        </Text>
      )}

      <Dialog open={isRemoveConfirmOpen} onOpenChange={setIsRemoveConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage id="lender.profile.removeEmploymentConfirmHeading" />
            </DialogTitle>
            <DialogDescription>
              <FormattedMessage id="lender.profile.removeEmploymentConfirmDescription" />
            </DialogDescription>
          </DialogHeader>

          {removeError && <AlertBanner status="error">{removeError}</AlertBanner>}

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsRemoveConfirmOpen(false)}
              disabled={isDeletingEmployment}
            >
              <FormattedMessage id="lender.profile.deleteAccountCancelButton" />
            </Button>
            <Button type="button" variant="destructive" onClick={handleRemove} disabled={isDeletingEmployment}>
              {isDeletingEmployment ? (
                <FormattedMessage id="lender.profile.removingEmploymentButton" />
              ) : (
                <FormattedMessage id="lender.profile.removeEmploymentButton" />
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
